import * as ngCore from '@angular/core'
import * as rxjsInterop from '@angular/core/rxjs-interop'
import * as ngxEditor from 'ngx-editor'

// TODO implement various configurations etc...

/**
 * Rich text editor component based on {@link https://sibiraj-s.github.io/ngx-editor/ ngx-editor}
 *
 *#### Inputs
 *- {@link Component.valueSg|value}?
 *
 *#### Outputs
 *- {@link Component.valueSg|valueChange}
 *
 * @example
 * ```html
 * <atn-ds-rich-text-editor" [value]="'<p>Hello World</p>'"/>
 *
 * <!-- Two way biding to signal model -->
 * <atn-ds-rich-text-editor" [(value)]="valueSg"/>
 * ```
 */
@ngCore.Component({
  imports: [
    ngxEditor.NgxEditorComponent,
    ngxEditor.NgxEditorFloatingMenuComponent,
  ],
  selector: 'atn-ds-rich-text-editor',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** TODO:  */
  public readonly editor = new ngxEditor.Editor({})
  /** TODO:  */
  private readonly _editorValueSg = rxjsInterop.toSignal(
    this.editor.valueChanges,
  )
  /**
   * Tracks whether the initial editor synchronization has occurred
   *
   * NOTE: This prevents the initial editor emission from overwriting the default value.
   * The rxjsInterop.toSignal() appears to emit immediately due to editor.valueChanges
   * being a BehaviorSubject, causing valueSg to be set to undefined on initialization.
   */
  private _isFirstSync = true
  /**
   * Synchronizes the {@link valueSg} with the editor content
   */
  private readonly _syncValueFromEditor = ngCore.effect(() => {
    const editorValue = this._editorValueSg()
    if (this._isFirstSync) {
      this._isFirstSync = false
      return
    }
    this.valueSg.set(editorValue ? ngxEditor.toHTML(editorValue) : undefined)
  })
  /**
   * Value of the rich text editor in HTML format
   *
   * @default undefined
   */
  public readonly valueSg = ngCore.model<string | undefined>(undefined, {
    alias: 'value',
  })
  /**
   * Synchronizes the {@link editor} content with the {@link valueSg}
   */
  private readonly _syncValueToEditor = ngCore.effect(() => {
    const value = this.valueSg()

    // Skip editor updates when the value change originated from the editor
    // itself
    ngCore.untracked(() => {
      const currentEditorValue = this._editorValueSg()
      const currentHtml = currentEditorValue
        ? ngxEditor.toHTML(currentEditorValue)
        : undefined
      // Only update editor if the content is actually different
      if (currentHtml === value) {
        return
      }

      // we usually use 'undefined' to represent empty value, but ngx-editor
      // expects '' for that, so we need to convert it
      this.editor.setContent(
        value
          ? ngxEditor.toDoc(value, undefined, {
              // Preserve whitespace to prevent unnecessary value changes from
              // automatic stripping
              preserveWhitespace: true,
            })
          : '',
      )
    })
  })
}
